import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  LabelList, ReferenceLine,
} from "recharts";
import type { UserRow } from "./sql";
import { FUNNEL_STEPS } from "./sql";
import { pctVal } from "../../lib/funnel/helpers";
import { PRIMARY, STEEL, MUTED, FG1 } from "../../lib/funnel/theme";
import { LegendDot } from "../../lib/funnel/charts";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

const PAID_LABEL = "Converted to paid";
const PLUS_LABEL = "Converted to Plus";
const PRO_LABEL = "Converted to Pro";

type LabelProps = {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: unknown;
};

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const renderDemoLabel = (format: (n: number) => string) => (props: LabelProps) => {
  const x = num(props.x), y = num(props.y), w = num(props.width), v = num(props.value);
  if (x == null || y == null || w == null || v == null) return null;
  if (v <= 0) return null;
  return (
    <text x={x + w / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={PRIMARY}>
      {format(v)}
    </text>
  );
};

// Recharts passes y = bar tip (bottom edge in SVG coords) for negative bars,
// so the label sits just below the bar tip at y + 14.
// YAxis padding={{bottom:20}} reserves space inside the clipPath for 100% bars.
const renderSelfLabel = (format: (n: number) => string) => (props: LabelProps) => {
  const x = num(props.x), y = num(props.y), w = num(props.width), v = num(props.value);
  if (x == null || y == null || w == null || v == null || v >= 0) return null;
  return (
    <text x={x + w / 2} y={y + 14} textAnchor="middle" fontSize={11} fontWeight={600} fill={STEEL}>
      {format(Math.abs(v))}
    </text>
  );
};

function wrapLines(text: string, maxChars: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (test.length > maxChars && current) { lines.push(current); current = word; }
    else { current = test; }
  }
  if (current) lines.push(current);
  return lines;
}

type TickProps = { x?: number | string; y?: number | string; payload?: { value: string } };

function makeTick(splitPaid: boolean, onTogglePaid: () => void) {
  // Shared per-render position cache so the Pro tick can draw a dotted line back
  // to the Plus tick. Recharts renders ticks in index order during the same pass,
  // so Plus has already populated its entry by the time Pro renders.
  const positions: { plus?: number; pro?: number } = {};
  return function WrappedTick({ x, y, payload }: TickProps) {
    const xn = typeof x === "number" ? x : Number(x) || 0;
    const yn = typeof y === "number" ? y : Number(y) || 0;
    const label = payload?.value ?? "";
    const lines = wrapLines(label, 14);
    const isToggle = label === PAID_LABEL || (splitPaid && (label === PLUS_LABEL || label === PRO_LABEL));
    const iconY = yn + 10 + lines.length * 14 + 2;
    const iconCy = iconY + 9;
    const iconClass = splitPaid ? "ti ti-arrows-join-2" : "ti ti-arrows-split-2";
    const iconTitle = splitPaid ? "Merge Plus + Pro back into Paid" : "Split into Plus and Pro";
    if (label === PLUS_LABEL) positions.plus = xn;
    if (label === PRO_LABEL) positions.pro = xn;
    const showLine = label === PRO_LABEL && positions.plus != null && positions.pro != null;
    return (
      <g>
        <text x={xn} y={yn + 10} textAnchor="middle" fontSize={11} fill={FG1}>
          {lines.map((line, i) => (
            <tspan key={i} x={xn} dy={i === 0 ? 0 : 14}>{line}</tspan>
          ))}
        </text>
        {showLine && (
          <line
            x1={(positions.plus ?? 0) + 12}
            y1={iconCy}
            x2={(positions.pro ?? 0) - 12}
            y2={iconCy}
            stroke={PRIMARY}
            strokeDasharray="3 3"
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        )}
        {isToggle && (
          <>
            <circle cx={xn} cy={iconCy} r={11} fill="#EDEEF1" stroke={PRIMARY} strokeWidth={1} />
            <foreignObject x={xn - 9} y={iconY} width={18} height={18} style={{ overflow: "visible" }}>
              <div
                onClick={onTogglePaid}
                title={iconTitle}
                style={{
                  cursor: "pointer",
                  color: PRIMARY,
                  fontSize: 14,
                  lineHeight: "18px",
                  textAlign: "center",
                  userSelect: "none",
                }}
              >
                <i className={iconClass} />
              </div>
            </foreignObject>
          </>
        )}
      </g>
    );
  };
}

type StepDatum = {
  step: string;
  demoVal: number;
  selfVal: number;
  demoPct: number;
  selfPct: number;
  demoCount: number;
  selfCount: number;
};

// Demo and self bars share the same denominator — signed-up users in the
// cohort — so they compare like-for-like. Phase 2 dropped the "Attended demo"
// first row; Dashboard 1 owns the attendee-side view.
export function ButterflyChart({
  users,
  title = "Activation funnel by sign-up path",
  subtitle = "Demo path: users who signed up after a demo. Self-serve: users who signed up without one. Both denominators are signed-up users in cohort.",
  infoContent,
  showAbs: showAbsProp,
  onShowAbsChange,
  selectedStep,
  onSelectStep,
  splitPaid: splitPaidProp,
  onSplitPaidChange,
  pagePills,
}: {
  users: UserRow[];
  title?: string;
  subtitle?: string;
  infoContent?: import("react").ReactNode;
  showAbs?: boolean;
  onShowAbsChange?: (next: boolean) => void;
  selectedStep?: string | null;
  onSelectStep?: (label: string | null) => void;
  splitPaid?: boolean;
  onSplitPaidChange?: (next: boolean) => void;
  pagePills?: FilterPillSpec[];
}) {
  const [splitPaidInternal, setSplitPaidInternal] = useState(false);
  const splitPaid = splitPaidProp ?? splitPaidInternal;
  const setSplitPaid = (updater: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof updater === "function" ? (updater as (p: boolean) => boolean)(splitPaid) : updater;
    if (onSplitPaidChange) onSplitPaidChange(next);
    else setSplitPaidInternal(next);
  };
  const [showAbsInternal, setShowAbsInternal] = useState(false);
  const showAbs = showAbsProp ?? showAbsInternal;
  const setShowAbs = (next: boolean) => {
    if (onShowAbsChange) onShowAbsChange(next);
    else setShowAbsInternal(next);
  };
  const demo = users.filter((u) => u.had_demo);
  const self = users.filter((u) => !u.had_demo);
  const selfTotal = self.length;
  const demoTotal = demo.length;

  const mkStep = (label: string, pred: (u: UserRow) => boolean): StepDatum => {
    const dCount = demo.filter(pred).length;
    const sCount = self.filter(pred).length;
    const dPct = pctVal(dCount, demoTotal);
    const sPct = pctVal(sCount, selfTotal);
    return {
      step: label,
      demoVal: showAbs ? dCount : dPct,
      selfVal: showAbs ? -sCount : -sPct,
      demoPct: dPct,
      selfPct: -sPct,
      demoCount: dCount,
      selfCount: sCount,
    };
  };

  const funnelSteps: StepDatum[] = FUNNEL_STEPS.flatMap((s) => {
    if (s.label === PAID_LABEL && splitPaid) {
      return [
        mkStep(PLUS_LABEL, (u) => u.on_paid_plan && u.billing_plan === "plus"),
        mkStep(PRO_LABEL, (u) => u.on_paid_plan && u.billing_plan === "pro"),
      ];
    }
    return [mkStep(s.label, s.predicate)];
  });

  // "Signed up" is the baseline now (100% on both sides — same denominator).
  const postSignupRows: StepDatum[] = [
    {
      step: "Signed up",
      demoVal: showAbs ? demoTotal : 100,
      selfVal: showAbs ? -selfTotal : -100,
      demoPct: 100,
      selfPct: -100,
      demoCount: demoTotal,
      selfCount: selfTotal,
    },
    ...funnelSteps,
  ];

  const maxMag = Math.max(
    ...postSignupRows.map((d) => Math.max(Math.abs(d.demoVal), Math.abs(d.selfVal))),
    showAbs ? 0 : 100,
  );
  // Pick a nice step (1/2/5 × 10^n) so ticks are evenly spaced and symmetric.
  const niceStep = (range: number): number => {
    if (range <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(range / 3)));
    const n = (range / 3) / mag;
    if (n < 1.5) return 1 * mag;
    if (n < 3) return 2 * mag;
    if (n < 7) return 5 * mag;
    return 10 * mag;
  };
  const step = niceStep(maxMag);
  const ticksPerSide = Math.max(1, Math.ceil(maxMag / step));
  const yMax = step * ticksPerSide;
  const yTicks: number[] = [];
  for (let i = -ticksPerSide; i <= ticksPerSide; i++) yTicks.push(i * step);

  const data: StepDatum[] = postSignupRows;
  const fmtVal = (n: number): string => (showAbs ? Math.round(n).toLocaleString() : `${n.toFixed(1)}%`);
  const fmtAxis = (n: number): string => (showAbs ? Math.round(n).toLocaleString() : `${n.toFixed(0)}%`);

  const pills: FilterPillSpec[] = [
    ...(pagePills ?? []),
    { kind: "mode", label: showAbs ? "Values: counts" : "Values: percentages" },
    { kind: "mode", label: splitPaid ? "Paid plan split: by tier" : "Paid plan split: combined" },
  ];

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {title}
            <span className="butterfly-info">
              <i className="ti ti-info-circle" />
              <div className="butterfly-info-pop" role="tooltip">
                {infoContent ?? (
                  <ul>
                    <li>
                      Both bars share the same denominator: signed-up users in cohort. Demo and self-serve are like-for-like.
                    </li>
                    <li>
                      Demo path = users with had_demo (lifetime email match into reporting.demo where is_demo=true).
                    </li>
                    <li>
                      &ldquo;Converted to paid&rdquo; is lifetime, including users who joined an already-paid workspace.
                    </li>
                  </ul>
                )}
              </div>
            </span>
          </span>
        }
        subtitle={subtitle}
        filterPills={pills}
        headerExtra={
          <div className="legend legend-wide" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap" }}>
            <LegendDot color={PRIMARY} label={`Demo path (n=${demoTotal})`} />
            <LegendDot color={STEEL} label={`Self-serve (n=${selfTotal})`} />
            <div
              role="group"
              aria-label="Value mode"
              style={{
                display: "inline-flex",
                border: `1px solid ${MUTED}`,
                borderRadius: 6,
                overflow: "hidden",
                fontSize: 11,
              }}
            >
              {([
                { key: false, label: "%" },
                { key: true, label: "#" },
              ] as const).map((opt) => {
                const active = showAbs === opt.key;
                return (
                  <button
                    key={String(opt.key)}
                    type="button"
                    onClick={() => setShowAbs(opt.key)}
                    style={{
                      padding: "3px 10px",
                      background: active ? PRIMARY : "transparent",
                      color: active ? "#fff" : FG1,
                      border: "none",
                      cursor: "pointer",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
      <div className="card-body">
        <ResponsiveContainer width="100%" height={540}>
          <BarChart data={data} margin={{ top: 28, right: 16, left: 16, bottom: 8 }} stackOffset="sign">
            <CartesianGrid strokeDasharray="4 4" stroke="rgba(0,0,0,0.15)" vertical={false} />
            <XAxis
              dataKey="step"
              tick={makeTick(splitPaid, () => setSplitPaid((v) => !v))}
              interval={0}
              height={80}
            />
            <YAxis
              domain={[-yMax, yMax]}
              ticks={yTicks}
              padding={{ top: 0, bottom: 20 }}
              tickFormatter={(v) => fmtAxis(Math.abs(Number(v)))}
              tick={{ fontSize: 10, fill: MUTED }}
              width={showAbs ? 56 : 42}
            />
            <ReferenceLine y={0} stroke={FG1} strokeWidth={1} />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
              formatter={(_value, name, item) => {
                const row = item?.payload as StepDatum | undefined;
                const isDemo = name === "demoVal";
                const count = (isDemo ? row?.demoCount : row?.selfCount) ?? 0;
                const pct = Math.abs(isDemo ? (row?.demoPct ?? 0) : (row?.selfPct ?? 0));
                const total = isDemo ? demoTotal : selfTotal;
                return [`${pct.toFixed(1)}% (${count.toLocaleString()}/${total.toLocaleString()})`, isDemo ? "Demo" : "Self"] as [string, string];
              }}
            />
            <Bar
              dataKey="demoVal"
              name="demoVal"
              stackId="path"
              fill={PRIMARY}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              onClick={(payload) => {
                const step = (payload as { step?: string } | undefined)?.step;
                if (!step || !onSelectStep) return;
                onSelectStep(selectedStep === step ? null : step);
              }}
              cursor={onSelectStep ? "pointer" : undefined}
            >
              <LabelList dataKey="demoVal" content={renderDemoLabel(fmtVal)} />
            </Bar>
            <Bar
              dataKey="selfVal"
              name="selfVal"
              stackId="path"
              fill={STEEL}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              onClick={(payload) => {
                const step = (payload as { step?: string } | undefined)?.step;
                if (!step || !onSelectStep) return;
                onSelectStep(selectedStep === step ? null : step);
              }}
              cursor={onSelectStep ? "pointer" : undefined}
            >
              <LabelList dataKey="selfVal" content={renderSelfLabel(fmtVal)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
